import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 徽章组件
 * 提供统一的徽章UI，用于状态标识、标签和计数显示
 *
 * @example
 * ```tsx
 * // 基本用法
 * <Badge>新</Badge>
 *
 * // 使用不同变体
 * <Badge colorVariant="success">激活</Badge>
 * <Badge colorVariant="danger">过期</Badge>
 * <Badge colorVariant="warning">即将到期</Badge>
 *
 * // 使用不同尺寸
 * <Badge size="small">小标签</Badge>
 * <Badge size="large">大标签</Badge>
 *
 * // 带图标的徽章
 * <Badge startIcon="✓">已验证</Badge>
 *
 * // 带关闭按钮的徽章
 * <Badge
 *   closable
 *   onClose={() => console.log('关闭')}
 * >
 *   可关闭
 * </Badge>
 * ```
 */
import { forwardRef } from 'react';
import { cn } from '../../utils/cn';
const COLOR_MAP = {
    // 参考 Button 'secondary'
    default: 'bg-gray-100 text-gray-800 border-gray-200',
    secondary: 'bg-gray-100 text-gray-800 border-gray-200',
    // 参考 Button 'outline' (text-static-sea-blue)
    primary: 'bg-static-eggshell text-static-sea-blue border-static-sea-blue',
    // 参考 Button 'primary' (text-interactive-emerald-flash)
    success: 'bg-static-eggshell text-interactive-emerald-flash border-interactive-emerald-flash/50',
    // 参考 Button 'outline' (hover:bg-static-beach-orange)
    warning: 'bg-static-eggshell text-static-beach-orange border-static-beach-orange',
    // 参考 Button 'danger' (text-interactive-coral-red)
    danger: 'bg-static-eggshell text-interactive-coral-red border-interactive-coral-red',
};
/**
 * 获取徽章尺寸样式
 */
const getSizeStyles = (size, dot) => {
    if (dot) {
        const dotSizes = {
            small: 'w-2 h-2',
            medium: 'w-3 h-3',
            large: 'w-4 h-4',
        };
        return dotSizes[size];
    }
    const sizes = {
        small: 'px-2 py-0.5 text-xs',
        medium: 'px-2.5 py-1 text-sm',
        large: 'px-3 py-1.5 text-base',
    };
    return sizes[size];
};
/**
 * 徽章组件
 *
 * 用于显示状态、标签或计数的小型UI元素
 */
export const Badge = forwardRef(({ colorVariant = 'default', size = 'medium', dot = false, startIcon, endIcon, closable = false, onClose, className, children, ...props }, ref) => {
    const baseStyles = dot
        ? 'inline-block rounded-full border'
        : 'inline-flex items-center gap-1 rounded-full border font-medium';
    const colorStyles = COLOR_MAP[colorVariant];
    const sizeStyles = getSizeStyles(size, dot);
    const badgeClasses = cn(baseStyles, colorStyles, sizeStyles, className);
    const handleClose = (e) => {
        e.stopPropagation();
        onClose?.();
    };
    // 圆点模式
    if (dot) {
        return _jsx("span", { ref: ref, className: badgeClasses, ...props });
    }
    return (_jsxs("span", { ref: ref, className: badgeClasses, ...props, children: [startIcon && _jsx("span", { className: "flex-shrink-0", children: startIcon }), children && _jsx("span", { children: children }), endIcon && _jsx("span", { className: "flex-shrink-0", children: endIcon }), closable && (_jsxs("button", { type: "button", className: "flex-shrink-0 ml-1 rounded-full hover:bg-gray-400/20 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-current", onClick: handleClose, "aria-label": "\u5173\u95ED", children: [_jsx("span", { className: "sr-only", children: "\u5173\u95ED" }), _jsx("svg", { className: "w-3 h-3", fill: "currentColor", viewBox: "0 0 20 20", children: _jsx("path", { fillRule: "evenodd", d: "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z", clipRule: "evenodd" }) })] }))] }));
});
Badge.displayName = 'Badge';
export default Badge;
//# sourceMappingURL=Badge.js.map